import {
  ApiError,
  type Cutover,
  type CutoverArea,
  type CutoverFinished,
  type CutoverFinishReply,
  type CutoverFinishState,
  type CutoverHeader,
  type CutoverOrgStep,
  type CutoverStep,
  type CutoverAreaWatch,
  type Readiness,
  type ReadinessCheck,
  type SalesAreas,
} from "./api";
import { clockTime, dateLabel } from "./format";
import { CHECK_TEXT, percentText } from "./parityHistory";
import { switchState } from "./salesAreas";
import type { SortColumn } from "./sort";

/** Design 26.11: the steps of an area, in order. */
export const STEPS: readonly CutoverStep[] = ["sheet", "shadow", "ready", "scheduled", "app", "watch_passed"];

export const STEP_TEXT: Record<CutoverStep, string> = {
  sheet: "Sheet",
  shadow: "Shadow",
  ready: "Ready",
  scheduled: "Scheduled",
  app: "On the app",
  watch_passed: "Watch passed",
};

export const ORG_STEP_TEXT: Record<CutoverOrgStep, string> = {
  T0: "T0 · Import",
  T1: "T1 · Shadow run",
  T2: "T2 · Cutover by area",
  T3: "T3 · Archive",
};

const SWITCHERS = "The administrator or the operations manager does this.";

/** The next action of the header in one sentence. A role that cannot do the step reads who does it. */
export function nextText(next: CutoverHeader["next"], can: { accept: boolean; switch: boolean } = { accept: true, switch: true }): string {
  const area = next.area ?? "";
  switch (next.action) {
    case "add_area":
      return "Add the first area on Admin > Sales areas.";
    case "import":
      return "Import the sheets each night until each account imports and each clean-up item has an owner. Then the nightly parity starts.";
    case "readiness":
      return can.accept
        ? `Make area ${area} ready: pass each check, or accept a fail with a reason.`
        : `Make area ${area} ready: pass each check. An administrator can accept a fail with a reason.`;
    case "schedule":
      return `Area ${area} is ready. Schedule its switch to the app.${can.switch ? "" : ` ${SWITCHERS}`}`;
    case "wait_switch":
      return `Area ${area} goes to the app on ${next.day ? dateLabel(next.day) : "its switch date"}.${can.switch ? " You can cancel it until that day." : ""}`;
    case "watch":
      return `Watch area ${area} for its first full cycle on the app.`;
    case "finish":
      return "Each area passed its watch. Finish the cutover.";
    case "done":
      return "The cutover is done. The sources are off.";
    default:
      return "";
  }
}

/** The watch column (roadmap 088). An API before 088 sends no watch: then a dash. */
export function watchText(watch: CutoverAreaWatch | null | undefined): string {
  if (!watch) return "—";
  if (watch.state === "passed") return "Passed";
  if (watch.state === "failed") return "Failed";
  if (watch.state === "open") {
    const lost = watch.lost_open ?? 0;
    return `Open${watch.cycle_end ? ` until ${dateLabel(watch.cycle_end)}` : ""}${lost ? ` · ${lost} lost ${lost === 1 ? "pickup" : "pickups"}` : ""}`;
  }
  return "Not started";
}

/** The switch date column. */
export function switchDateText(area: Pick<CutoverArea, "switch_state" | "app_from">): string {
  if (area.switch_state === "scheduled") return area.app_from ? dateLabel(area.app_from) : "A later date";
  if (area.switch_state === "app") return area.app_from ? `Since ${dateLabel(area.app_from)}` : "On the app";
  return "—";
}

/** The short value beside a check chip in the table. A check that passes needs none. */
export function checkShortText(check: ReadinessCheck): string {
  if (check.passed) return "";
  if (check.key === "parity") return check.of === null ? "no history" : percentText(check.share);
  if (check.key === "import") return check.done ? `${check.done} ${check.done === 1 ? "error" : "errors"}` : "";
  if (check.key === "drivers") return check.of ? `${check.done ?? 0} of ${check.of}` : "no driver";
  if (check.key === "accounts") return check.of ? percentText(check.share) : "no accounts";
  return "";
}

/** The names of the checks that fail with no accept, in the order of the page. */
export const failingChecks = (area: Pick<CutoverArea, "readiness">): string[] => (area.readiness?.checks ?? []).filter((check) => !check.ok).map((check) => CHECK_TEXT[check.key]);

/** Rule T-12: the warning of the switch panel for an area that is not ready, else null. */
export function notReadyText(area: Pick<CutoverArea, "code" | "readiness">): string | null {
  if (!area.readiness || area.readiness.ready) return null;
  const failing = failingChecks(area);
  if (!failing.length) return `${area.code} is not ready.`;
  return `${area.code} is not ready: ${failing.length === 1 ? "1 check fails" : `${failing.length} checks fail`} (${failing.join(", ")}).`;
}

/** The second question before the switch of an area that is not ready (rule T-12). */
export const notReadyQuestion = (code: string) => `Area ${code} is not ready. Switch it to the app all the same?`;

export const readyShareText = (area: Pick<CutoverArea, "readiness">) => (area.readiness ? percentText(area.readiness.ready_share) : "–");

/** The step counts of the header, for example "Shadow 30 · Ready 2". Steps with no area do not show. */
export function stepCountsText(steps: Partial<Record<CutoverStep, number>>): string {
  return STEPS.filter((step) => steps[step])
    .map((step) => `${STEP_TEXT[step]} ${steps[step]}`)
    .join(" · ");
}

export const CUTOVER_COLUMNS: SortColumn<CutoverArea>[] = [
  { key: "area", label: "Area", value: (row) => row.code },
  { key: "step", label: "Step", value: (row) => row.step, order: STEPS },
  { key: "parity", label: "Parity", value: (row) => row.parity?.latest?.share ?? null, first: "desc" },
  { key: "ready", label: "Ready", value: (row) => row.readiness?.ready_share ?? null, first: "desc", num: true },
  { key: "date", label: "Switch date", value: (row) => (row.switch_state === "sheet" ? null : row.app_from) },
  { key: "watch", label: "Watch", value: (row) => row.watch?.state ?? null },
];

/** An API before roadmap 085 has no /api/cutover: the page uses the sales areas and the readiness. */
export function fromSalesAreas(found: SalesAreas, ready: Readiness | null): Cutover {
  const checks = new Map((ready?.areas ?? []).map((area) => [area.area, area]));
  const areas = found.areas.map((area): CutoverArea => {
    const state = switchState(area);
    const own = checks.get(area.code);
    const step: CutoverStep = state === "app" ? "app" : state === "scheduled" ? "scheduled" : own?.ready ? "ready" : "sheet";
    return {
      id: area.id,
      code: area.code,
      region: area.region,
      rotation_week: area.rotation_week,
      pickup_source: area.pickup_source ?? "sheet",
      app_from: area.app_from ?? null,
      switch_state: state,
      step,
      accounts: own?.accounts ?? 0,
      parity: null,
      readiness: own ? { checks: own.checks, ready: own.ready, ready_share: own.ready_share } : null,
      watch: null,
      can_switch: Boolean(area.can_switch),
    };
  });
  return {
    header: null,
    areas,
    days: [],
    import_run: ready?.import_run ?? null,
    gate: ready?.gate ?? { cycles: 2, share: 0.95, tolerance: 0.1 },
    checks: ready?.checks ?? [],
    cycle_start: "",
    can_accept: Boolean(ready?.can_accept),
    can_parity: ready !== null,
    can_export: false,
  };
}

/** The question before the finish (design 26.11 F9). */
export const finishQuestion = (org: string) => `Finish the cutover of ${org}? Each import source goes off.`;

/** The Finished line: the date and the person. An older name field can be missing. */
export function finishedText(finished: Pick<CutoverFinished, "at" | "by_name">): string {
  const day = finished.at.slice(0, 10);
  return `Finished on ${day}${finished.by_name ? ` by ${finished.by_name}` : ""}. Each import source is off. The runs and the issues of the import stay.`;
}

/** The finish button shows only to the administrator, when each area passed and no finish holds. */
export function canFinish(page: Pick<Cutover, "can_finish" | "finished" | "header" | "finish">): boolean {
  return Boolean(page.can_finish) && !page.finished && page.finish?.state !== "running" && page.header?.next.action === "finish";
}

/** How often the page reads the state again while the admin job finishes (SO-4232). */
export const FINISH_POLL_MS = 5000;

/** An older API finishes in the request: its reply holds `finished`. A newer one says "running". */
export function finishReplyState(reply: CutoverFinishReply): "done" | "running" {
  return reply.finished || reply.state !== "running" ? "done" : "running";
}

/** The line while the job runs: "Finishing… (started 7:10 AM EDT by Example Admin)". */
export function finishRunningText(state: Pick<CutoverFinishState, "at" | "by_name">, timeZone?: string): string {
  const at = new Date(state.at);
  const when = Number.isNaN(at.getTime()) ? "" : `started ${clockTime(at, timeZone)}`;
  const who = state.by_name ? `by ${state.by_name}` : "";
  const detail = [when, who].filter(Boolean).join(" ");
  return `Finishing…${detail ? ` (${detail})` : ""} Each import source stays on until the finish ends.`;
}

const FINISH_CODE_TEXT: Record<string, string> = {
  job_not_started: "The finish did not start.",
  no_answer: "The finish gave no answer for a long time.",
  not_allowed: "Only an administrator can finish the cutover.",
  no_area: "The organization has no area.",
  area_not_passed: "An area has not passed its watch now.",
  sources_off: "Each import source was off already.",
  import_running: "An import ran at the same time.",
  report_run_failed: "The last report run did not read the source.",
  report_run_error: "The last report run stopped with an error.",
  export_error: "The export was not made.",
  export_not_saved: "The export was not saved.",
  error: "The finish stopped with an error.",
};

/** The failure in plain words. It says "Try again" only when the page shows the button to try again. */
export function finishFailedText(state: Pick<CutoverFinishState, "code">, canRetry: boolean): string {
  const why = (state.code && FINISH_CODE_TEXT[state.code]) || "The finish did not complete.";
  return `${why} No import source changed. ${canRetry ? "Try again, or reload the page to see the areas now." : "Reload the page to see the areas now."}`;
}

/** The status line when a running finish ends. A failure points to its line, so that its text does not show two times. */
export function finishEndedText(page: Pick<Cutover, "finished" | "finish">): { ok: boolean; text: string } | null {
  if (page.finished) return { ok: true, text: "The cutover is finished. Each import source is off." };
  if (page.finish?.state === "failed") return { ok: false, text: "The finish stopped. The reason is under Next." };
  return null;
}

/** "1 account", "2,100 accounts". */
export const accountsText = (count: number) => `${count.toLocaleString("en-US")} ${count === 1 ? "account" : "accounts"}`;

/** Plain words for a failed finish, as importFailure, never the HTTP text. */
export function finishFailure(e: unknown, online = typeof navigator === "undefined" || navigator.onLine): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (!status) return online ? "The server did not answer. Check your connection, then try again." : "You are offline. Connect, then try again.";
  if (status === 401) return "Your session ended. Sign in again, then try again.";
  if (status === 403) return "Only an administrator can finish the cutover.";
  if (status === 404) return "This server cannot finish the cutover yet.";
  if (status === 409) return e instanceof ApiError && e.detail ? e.detail : "Each area must pass its watch first. Reload the page to see the areas now.";
  if (status === 502) return e instanceof ApiError && e.detail ? e.detail : "The finish did not complete. Try again in a few minutes.";
  if (status >= 500) return "The server did not answer. Try again in a few minutes.";
  return "The cutover did not finish. Reload the page, then try again.";
}
